import React from 'react';
import EmployeeName from '../EmployeeName';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
  Chip,
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  alpha,
  TablePagination,
  useMediaQuery,
} from '@mui/material';
import dayjs from 'dayjs';
import { FA_STATUS } from 'utils/constants/FaStatus.constant';
import _ from 'lodash';

export default function FieldAllowanceRequestTable({
  faRequest,
  page,
  setPage,
  size,
  setSize,
  handleDelete,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const hasData = faRequest?.rows && faRequest.rows.length > 0;

  const getStatusColor = (status) => FA_STATUS?.[status]?.color || 'default';
  const getStatusLabel = (status) => FA_STATUS?.[status]?.description || status;

  const handleChangePage = (event, newPage) => setPage(newPage + 1);
  const handleChangeRowsPerPage = (event) => {
    setSize(event.target.value);
    setPage(1);
  };

  if (!hasData) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            ยังไม่มีคำขอค่าออกสนาม
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (isMobile) {
    return (
      <Stack spacing={2}>
        {faRequest.rows.map((row, index) => (
          <Card key={row.id} variant="outlined" sx={{ borderRadius: 3, p: 0, overflow: 'hidden' }}>
            <Box
              sx={{
                bgcolor: FA_STATUS?.[row?.status]?.color
                  ? theme.palette[FA_STATUS[row.status].color]?.main || theme.palette.grey[300]
                  : theme.palette.grey[300],
                px: 2, py: 1.2,
              }}
            >
              <Chip
                label={getStatusLabel(row?.status)}
                color={getStatusColor(row?.status)}
                size="small"
                variant="filled"
                sx={{ fontWeight: 600, bgcolor: 'inherit', color: '#fff' }}
              />
            </Box>
            <CardContent sx={{ p: 2 }}>
              <Stack spacing={1.2}>
                <Typography variant="subtitle2" color="primary" fontWeight={700}>
                  #{index + 1} {row?.project?.project_number || 'N/A'} - {row?.project?.name}
                </Typography>
                <Typography variant="body2" fontWeight={600}>วันที่ {dayjs(row?.date).format('DD/MM/BBBB')}</Typography>
                <Box>
                  <Typography variant="body2" fontWeight={600}>พนักงาน</Typography>
                  <Stack spacing={0.5}>
                    {_?.map(row?.fa_lists, (e, idx) => (
                      <Typography key={e?._id || idx} variant="body2">
                        {idx + 1}. <EmployeeName id={e?.employee} name={e?.employee_name} /> — {getStatusLabel(e?.status)}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
                <Typography variant="body2" color="text.secondary">{row?.description || 'ไม่มีรายละเอียด'}</Typography>
                <Button
                  variant="outlined" size="small" color="error"
                  onClick={() => handleDelete(row?._id)}
                  disabled={row?.status !== 'PENDING'}
                  sx={{ mt: 1, alignSelf: 'flex-end' }}
                >
                  ลบ
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
              <TableCell sx={{ color: '#FFFFFF', width: 60 }}>ลำดับ</TableCell>
              <TableCell sx={{ color: '#FFFFFF', width: 180 }}>พนักงาน</TableCell>
              <TableCell sx={{ color: '#FFFFFF', width: 140 }}>โปรเจค</TableCell>
              <TableCell sx={{ color: '#FFFFFF', width: 200 }}>รายละเอียด</TableCell>
              <TableCell sx={{ color: '#FFFFFF', width: 110 }}>วันที่</TableCell>
              <TableCell sx={{ color: '#FFFFFF', width: 130 }}>สถานะ</TableCell>
              <TableCell align="center" sx={{ color: '#FFFFFF', width: 80 }}>จัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {faRequest.rows.map((row, index) => (
              <TableRow key={row.id} sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                <TableCell><Typography variant="body2">{index + 1}</Typography></TableCell>
                <TableCell>
                  <Box>
                    {_?.map(row?.fa_lists, (e, idx) => (
                      <Typography key={e?._id || idx} variant="body2">
                        {idx + 1}. <EmployeeName id={e?.employee} name={e?.employee_name} />
                        {' '}
                        <Chip label={getStatusLabel(e?.status)} color={getStatusColor(e?.status)} size="small" sx={{ height: 18, fontSize: 10, ml: 0.5 }} />
                      </Typography>
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={theme.palette.info.main}>{row?.project?.project_number || 'N/A'}</Typography>
                  <Typography variant="caption" color="text.secondary">{row?.project?.name || 'ไม่ระบุโปรเจค'}</Typography>
                </TableCell>
                <TableCell>{row?.description || 'ไม่มีรายละเอียด'}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600} color={theme.palette.success.main}>
                    {dayjs(row?.date).format('DD/MM/BBBB')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={getStatusLabel(row?.status)} color={getStatusColor(row?.status)} size="small" />
                </TableCell>
                <TableCell align="center">
                  <Button
                    variant="outlined" size="small" color="error"
                    onClick={() => handleDelete(row?._id)}
                    disabled={row?.status !== 'PENDING'}
                  >
                    ลบ
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 30, 40, 50, 100]}
          component="div"
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          count={faRequest?.total || 0}
          rowsPerPage={size}
          page={page - 1}
        />
      </TableContainer>
    </Card>
  );
}
